import { useState, useEffect } from 'react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Tag, Plus, Pencil, Trash2, ArrowUpRight, ArrowDownRight, Palette, RefreshCw
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { getErrorMessage } from '@/utils/errorHandler';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const colorOptions = [
  { value: 'gray', label: 'Gray', class: 'bg-gray-100 text-gray-700' },
  { value: 'red', label: 'Red', class: 'bg-red-100 text-red-700' },
  { value: 'orange', label: 'Orange', class: 'bg-orange-100 text-orange-700' },
  { value: 'amber', label: 'Amber', class: 'bg-amber-100 text-amber-700' },
  { value: 'yellow', label: 'Yellow', class: 'bg-yellow-100 text-yellow-700' },
  { value: 'green', label: 'Green', class: 'bg-green-100 text-green-700' },
  { value: 'teal', label: 'Teal', class: 'bg-teal-100 text-teal-700' },
  { value: 'cyan', label: 'Cyan', class: 'bg-cyan-100 text-cyan-700' },
  { value: 'blue', label: 'Blue', class: 'bg-blue-100 text-blue-700' },
  { value: 'indigo', label: 'Indigo', class: 'bg-indigo-100 text-indigo-700' },
  { value: 'purple', label: 'Purple', class: 'bg-purple-100 text-purple-700' },
  { value: 'pink', label: 'Pink', class: 'bg-pink-100 text-pink-700' },
];

const getColorClass = (color) => {
  const found = colorOptions.find(c => c.value === color);
  return found ? found.class : 'bg-gray-100 text-gray-700';
};

export default function Categories() {
  const [loading, setLoading] = useState(true);
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [incomeCategories, setIncomeCategories] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [categoryType, setCategoryType] = useState('expense');
  const [form, setForm] = useState({
    label: '',
    value: '',
    color: 'gray'
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/finance/categories`);
      setExpenseCategories(response.data.expense_categories || []);
      setIncomeCategories(response.data.income_categories || []);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load categories'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);

    try {
      const payload = {
        ...form,
        value: form.value || form.label.toLowerCase().replace(/\s+/g, '_'),
        type: categoryType
      };

      if (editingCategory) {
        await axios.put(`${API_URL}/finance/categories/${editingCategory.id}`, payload);
        toast.success('Category updated');
      } else {
        await axios.post(`${API_URL}/finance/categories`, payload);
        toast.success('Category created');
      }

      setShowDialog(false);
      resetForm();
      fetchCategories();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save category'));
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (category) => {
    if (!confirm(`Delete category "${category.label}"? Existing transactions will keep their category but new ones won't have this option.`)) return;

    try {
      await axios.delete(`${API_URL}/finance/categories/${category.id}`);
      toast.success('Category deleted');
      fetchCategories();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete category'));
    }
  };

  const openEditDialog = (category, type) => {
    setEditingCategory(category);
    setCategoryType(type);
    setForm({
      label: category.label,
      value: category.value,
      color: category.color || 'gray'
    });
    setShowDialog(true);
  };

  const openAddDialog = (type) => {
    resetForm();
    setCategoryType(type);
    setShowDialog(true);
  };

  const resetForm = () => {
    setEditingCategory(null);
    setForm({
      label: '',
      value: '',
      color: 'gray'
    });
  };

  const renderCategoryTable = (categories, type) => (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Label</TableHead>
          <TableHead>Value (ID)</TableHead>
          <TableHead>Color</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {categories.length === 0 ? (
          <TableRow>
            <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
              No categories found. Add one to get started.
            </TableCell>
          </TableRow>
        ) : (
          categories.map((cat) => (
            <TableRow key={cat.id || cat.value}>
              <TableCell className="font-medium">{cat.label}</TableCell>
              <TableCell>
                <code className="text-xs bg-muted px-2 py-1 rounded">{cat.value}</code>
              </TableCell>
              <TableCell>
                <Badge className={getColorClass(cat.color)}>
                  {cat.color || 'gray'}
                </Badge>
              </TableCell>
              <TableCell className="text-right">
                <div className="flex items-center justify-end gap-1">
                  <Button variant="ghost" size="sm" onClick={() => openEditDialog(cat, type)} data-testid={`edit-${cat.value}-btn`}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  {cat.id && (
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(cat)} className="text-red-600 hover:text-red-700" data-testid={`delete-${cat.value}-btn`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="categories-page">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Categories</h1>
            <p className="text-muted-foreground mt-1">Manage expense and income categories</p>
          </div>
          <Button variant="outline" onClick={fetchCategories} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <RefreshCw className="h-8 w-8 mx-auto mb-4 animate-spin text-muted-foreground" />
            <p className="text-muted-foreground">Loading categories...</p>
          </div>
        ) : (
          <Tabs defaultValue="expense" className="space-y-4">
            <TabsList>
              <TabsTrigger value="expense" className="gap-2">
                <ArrowDownRight className="h-4 w-4 text-red-600" />
                Expense Categories
              </TabsTrigger>
              <TabsTrigger value="income" className="gap-2">
                <ArrowUpRight className="h-4 w-4 text-green-600" />
                Income Categories
              </TabsTrigger>
            </TabsList>

            <TabsContent value="expense">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <ArrowDownRight className="h-5 w-5 text-red-600" />
                      Expense Categories
                    </CardTitle>
                    <CardDescription>Categories for tracking business expenses</CardDescription>
                  </div>
                  <Button onClick={() => openAddDialog('expense')} data-testid="add-expense-category-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Category
                  </Button>
                </CardHeader>
                <CardContent className="p-0">
                  {renderCategoryTable(expenseCategories, 'expense')}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="income">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <ArrowUpRight className="h-5 w-5 text-green-600" />
                      Income Categories
                    </CardTitle>
                    <CardDescription>Categories for tracking business income</CardDescription>
                  </div>
                  <Button onClick={() => openAddDialog('income')} className="bg-green-600 hover:bg-green-700" data-testid="add-income-category-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Category
                  </Button>
                </CardHeader>
                <CardContent className="p-0">
                  {renderCategoryTable(incomeCategories, 'income')}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        )}

        {/* Add/Edit Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Tag className="h-5 w-5" />
                {editingCategory ? 'Edit Category' : 'Add Category'}
              </DialogTitle>
              <DialogDescription>
                {editingCategory 
                  ? `Update the ${categoryType} category details` 
                  : `Create a new ${categoryType} category`}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="label">Display Label *</Label>
                  <Input
                    id="label"
                    value={form.label}
                    onChange={(e) => setForm({ ...form, label: e.target.value })}
                    placeholder="e.g., Fuel, Trip Payment"
                    required
                    data-testid="category-label-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="value">Value ID</Label>
                  <Input
                    id="value"
                    value={form.value}
                    onChange={(e) => setForm({ ...form, value: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                    placeholder="Auto-generated from label"
                    data-testid="category-value-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Used internally. Will be auto-generated from label if left empty.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Color</Label>
                  <Select value={form.color} onValueChange={(value) => setForm({ ...form, color: value })}>
                    <SelectTrigger data-testid="category-color-select">
                      <div className="flex items-center gap-2">
                        <div className={`h-4 w-4 rounded ${getColorClass(form.color).split(' ')[0]}`} />
                        <SelectValue />
                      </div>
                    </SelectTrigger>
                    <SelectContent>
                      {colorOptions.map((color) => (
                        <SelectItem key={color.value} value={color.value}>
                          <div className="flex items-center gap-2">
                            <div className={`h-4 w-4 rounded ${color.class.split(' ')[0]}`} />
                            {color.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-sm font-medium mb-2">Preview:</p>
                  <Badge className={getColorClass(form.color)}>
                    {form.label || 'Category Name'}
                  </Badge>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={formLoading} 
                  className={categoryType === 'income' ? 'bg-green-600 hover:bg-green-700' : ''}
                  data-testid="save-category-btn"
                >
                  {formLoading ? 'Saving...' : (editingCategory ? 'Update' : 'Create')}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
